import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './countdown-timer.style.scss';
import { formatDate } from '@thd-olt-functional/utils';
import helpers from '../helpers/countdownHelper';
import TimerInline from './partials/TimerInline';
import TimerBlock from './partials/TimerBlock';

const CountdownTimer = ({
  endDate,
  timebombThreshold,
  daysLeftThreshold,
  dateDisplayThreshold,
  hours,
  minutes,
  shouldShowSecondsRemaining,
  shouldUseMessagingString,
  shouldIncludeSvg,
  shouldUseBlock,
  shouldUseClockDisplay,
  shouldUseDateDisplay,
  shouldUseDaysLeftDisplay,
  customMessagingString,
  isSBOTD,
  isPromotionTimer
}) => {
  const initializeEndDate = () => {
    if (endDate) {
      return new Date(endDate);
    }
    if (hours || minutes) {
      const tempDate = new Date();
      if (hours) {
        tempDate.setHours(tempDate.getHours() + +hours);
      }
      if (minutes) {
        tempDate.setMinutes(tempDate.getMinutes() + +minutes);
      }
      tempDate.setSeconds(59);
      return tempDate;
    }
    return 0;
  };

  const [timerEndDate, setTimerEndDate] = useState(initializeEndDate());

  const calculateTimeLeft = () => {
    if (!timerEndDate) {
      return 0;
    }
    let newTimeLeft = null;
    if (timerEndDate && isSBOTD) {
      newTimeLeft = helpers.calculateSbotdTimeLeft();
    }
    if (isPromotionTimer && timerEndDate) {
      newTimeLeft = helpers.calculatePromotionTimeLeft(timerEndDate);
    }
    if (!isSBOTD && !isPromotionTimer) {
      const timeToExpiration = +new Date(timerEndDate) - +new Date();

      if (timeToExpiration > 0) {
        newTimeLeft = {
          days: helpers.convertToDays(timeToExpiration),
          timerHours: helpers.convertToHours(timeToExpiration),
          timerMinutes: helpers.convertToMinutes(timeToExpiration),
          seconds: helpers.convertToSeconds(timeToExpiration),
          totalMilliseconds: timeToExpiration
        };
      }
    }

    return newTimeLeft;
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  const getTimeStringDate = () => {

    return formatDate({ date: timerEndDate, shortMonth: true, dayMonthDateFormat: true });
  };

  const getTimeStringDaysHoursMinutes = () => {

    const daySuffix = (timeLeft || {}).days === 1 ? ' day' : ' days';
    const hourSuffix = (timeLeft || {}).timerHours === 1 ? ' hr' : ' hours';
    const minuteSuffix = (timeLeft || {}).timerMinutes === 1 ? ' min' : ' mins';
    const secondSuffix = (timeLeft || {}).seconds === 1 ? ' sec' : ' secs';

    const days = timeLeft?.days ? `${timeLeft.days}${(daySuffix)}` : '';
    const timerHours = timeLeft?.timerHours ? ` ${timeLeft.timerHours}${(hourSuffix)}` : '';
    const timerMinutes = timeLeft?.timerMinutes ? ` ${timeLeft.timerMinutes}${(minuteSuffix)}` : '';
    const timerSeconds = shouldShowSecondsRemaining && timeLeft?.seconds ? ` ${timeLeft.seconds}${(secondSuffix)}` : '';
    return `${days}${timerHours}${timerMinutes}${timerSeconds}`;
  };

  const getTimeStringDigitalClock = () => {

    let newTimeString = '';

    const { days, timerHours, timerMinutes, seconds } = timeLeft;
    /* eslint-disable max-len */
    newTimeString += `${((days * 24 + timerHours) >= 10 ? days * 24 + timerHours : '0'.concat(timerHours))}`;
    newTimeString += `:${(timerMinutes >= 10 ? timerMinutes : '0'.concat(timerMinutes))}`;
    newTimeString += `:${(seconds >= 10 ? seconds : '0'.concat(seconds))}`;
    /* eslint-enable max-len */
    return newTimeString;
  };

  const getTimeStringDaysLeft = () => {

    let newTimeString = '';

    newTimeString += `${timeLeft.days}${(timeLeft.days === 1 ? ' day' : ' days')}`;
    return newTimeString;
  };

  const getPromotionTimer = () => {

    const timerDays = timeLeft.days === 0 ? '' : timeLeft.days.toString();
    const timerHours = timeLeft.hours.toString().padStart(timeLeft.days > 0 ? 2 : 1, '0');
    const timerMinutes = timeLeft.minutes.toString().padStart(2, '0');
    const timerSeconds = timeLeft.seconds.toString().padStart(2, '0');

    const daysText = <span className="countdown-timer-text">{timerDays === 1 ? 'day : ' : 'days : '}</span>;
    const hoursText = <span className="countdown-timer-text">{timeLeft.hours === 1 ? 'hr " ' : 'hrs : '}</span>;
    const minuteText = (
      <span className="countdown-timer-text">
        {timeLeft.minutes === 1 ? 'min' : 'mins'}{!timerDays ? ' : ' : ''}
      </span>
    );
    const secondsText = <span className="countdown-timer-text">{timeLeft.seconds === 1 ? 'sec' : 'secs'}</span>;

    const promotionMessage = timeLeft.totalMilliseconds >= 0 && (
      <>
        {timerDays}{timerDays ? daysText : ''}{timerHours}{hoursText}
        {timerMinutes}{minuteText}{!timerDays ? timerSeconds : ''}{!timerDays ? secondsText : ''}
      </>
    );

    return promotionMessage;
  };

  const getTimeStringAndMessaging = () => {

    if (isSBOTD && shouldShowSecondsRemaining && timeLeft) {
      return { timeString: timeLeft };
    }

    if (isPromotionTimer && timeLeft) {
      return { messagingString: customMessagingString || '', timeString: getPromotionTimer() };
    }

    if (!shouldUseClockDisplay && !shouldUseDateDisplay) {
      return { messagingString: customMessagingString || 'Ends in ', timeString: getTimeStringDaysHoursMinutes() };
    }

    if (!(timeLeft && timeLeft.totalMilliseconds)) {
      return { messagingString: null, timeString: null };
    }

    if (shouldUseClockDisplay && (timeLeft.totalMilliseconds <= timebombThreshold)) {
      return { messagingString: customMessagingString || 'Ends in ', timeString: getTimeStringDigitalClock() };
    }

    if (shouldUseDaysLeftDisplay && (timeLeft.totalMilliseconds < daysLeftThreshold)) {
      return { messagingString: customMessagingString || 'Ends in ', timeString: getTimeStringDaysLeft() };
    }

    if (shouldUseDateDisplay && (timeLeft.totalMilliseconds < dateDisplayThreshold)) {
      return { messagingString: customMessagingString || 'Ends on ', timeString: getTimeStringDate() };
    }

    return { messagingString: null, timeString: null };
  };

  const [timeStringAndMessaging, setTimeStringAndMessaging] = useState(getTimeStringAndMessaging());

  useEffect(() => {

    const timeout = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
      setTimeStringAndMessaging((getTimeStringAndMessaging()));
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [timeLeft]);

  if (!(timerEndDate && timeLeft && timeStringAndMessaging && timeStringAndMessaging.timeString)) {
    return null;
  }

  return shouldUseBlock ? (
    <TimerBlock
      timeStringAndMessaging={timeStringAndMessaging}
      shouldIncludeSvg={shouldIncludeSvg}
      shouldUseMessagingString={shouldUseMessagingString}
    />
  ) : (
    <TimerInline
      timeStringAndMessaging={timeStringAndMessaging}
      shouldIncludeSvg={shouldIncludeSvg}
      shouldUseMessagingString={shouldUseMessagingString}
    />
  );
};

CountdownTimer.propTypes = {
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  timebombThreshold: PropTypes.number,
  daysLeftThreshold: PropTypes.number,
  dateDisplayThreshold: PropTypes.number,
  hours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minutes: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shouldUseMessagingString: PropTypes.bool,
  shouldShowSecondsRemaining: PropTypes.bool,
  shouldIncludeSvg: PropTypes.bool,
  shouldUseBlock: PropTypes.bool,
  shouldUseClockDisplay: PropTypes.bool,
  shouldUseDateDisplay: PropTypes.bool,
  shouldUseDaysLeftDisplay: PropTypes.bool,
  customMessagingString: PropTypes.string,
  isSBOTD: PropTypes.bool,
  isPromotionTimer: PropTypes.bool,
};

CountdownTimer.defaultProps = {
  endDate: null,
  hours: 0,
  minutes: 0,
  timebombThreshold: 0,
  daysLeftThreshold: 0,
  dateDisplayThreshold: 0,
  shouldUseMessagingString: false,
  shouldShowSecondsRemaining: false,
  shouldIncludeSvg: false,
  shouldUseBlock: false,
  shouldUseClockDisplay: false,
  shouldUseDateDisplay: false,
  shouldUseDaysLeftDisplay: false,
  customMessagingString: null,
  isSBOTD: false,
  isPromotionTimer: false,
};

export default CountdownTimer;
